<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from "vue";

import type { Headers, PaginationMeta } from "@/types/rest";
import type { ChangeCallbackParams } from "@/types/config";
import type {
  ConfiguredEntity,
  IntegrationInstance,
  EntityFilterData
} from "@/types/integrationInstance";

import type { Macro } from "@/types/macro";
import type {
  IncludedEntity,
} from "@/types/activity";
import { EntityType } from "@/types/enums";

import { macrosStore } from "@/stores/macros";
import { integrationsStore } from "@/stores/integrations";
import { addErrorBottom } from "@/stores/messages";

import { getDefaultEntityIcon } from "@/mixins/entity";
import {
  getCurrentLocale,
  getValueByLang
} from "@/mixins/translatedProperty";
import { useComponentErrorCapture } from "@/mixins/error";
import router from "@/mixins/router";
import { useDataHelper } from "@/mixins/dataHelper";
import { getPaginationLimit, savePaginationLimit } from "@/mixins/listing";
import { isTouchEnabled } from "@/mixins/device";

import UCInput from "@/components/ui/UCInput.vue";
import IconSelect from "@/components/elements/icon/IconSelect.vue";
import IncludedEntities from "@/components/elements/entity/IncludedEntities.vue";
import EntityListFiltered from "@/components/elements/entity/EntityListFiltered.vue";

useComponentErrorCapture();
const { updateObjectByKeys, standardizeLangTexts } = useDataHelper();

const storage = macrosStore();
const integrationsStorage = integrationsStore();

const props = defineProps({
  macroId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["changeButton"]);

const macro = ref<Macro | null>(null);
const macroValues = ref<Record<string, any>>({});

const showAvailableEntities = ref(false);

const allEntities = ref<ConfiguredEntity[]>([]);
const filteredEntities = ref<ConfiguredEntity[]>([]);
const selectedEntities = ref<IncludedEntity[]>([]);
const instances = ref<IntegrationInstance[]>([]);

const macroIncludedList = ref();

const entityListFilter = ref(<EntityFilterData>{
  searchText: '',
  entityTypes: '',
  instances: ''
});

const pagination = ref<PaginationMeta>({
  limit: getPaginationLimit() ?? 20,
  page: 1,
});

const loading = ref(false);
const macroAvailableEntities = ref();

storage.$onAction(({ name, args, after }) => {
  if (name !== "socketUpdate") {
    return;
  }
  after(async () => {
    const { entity_id, event_type } = args[0];
    if (entity_id !== props.macroId) {
      return;
    }
    if (event_type === "DELETE") {
      router.push({
        name: "activities-macros",
      });
    } else if (
      entity_id === props.macroId &&
      event_type === "CHANGE" &&
      (args[0] && args[0].new_state) 
    ) {
      const updMacro = updateObjectByKeys(JSON.parse(JSON.stringify(macro.value)), args[0].new_state);
      setMacro(updMacro);
    }
  });
});

watch(
  () => pagination.value.limit,
  (val) => {
    savePaginationLimit(val);
  }
);

integrationsStorage.$subscribe((mutation, state) => {
  allEntities.value = state.configuredEntities;
  instances.value = state.instances;
});

watch(showAvailableEntities, (newVal) => {
  if (newVal) {
    nextTick(() => {
      const container = macroAvailableEntities.value as HTMLElement;
      if (!isTouchEnabled() && container) {
        const firstInput = container.querySelector('input') as HTMLElement;
        firstInput?.focus();
      }
    });
  }
});

function setMacro(newValue: Macro, onInit: boolean = false) {
  const nameLang = macroValues.value.name?.langCode ?? getCurrentLocale();
  const descLang = macroValues.value.description?.langCode ?? getCurrentLocale();

  macro.value = JSON.parse(JSON.stringify(newValue)) as Macro;

  const macroName = getValueByLang(newValue.name, nameLang, !onInit);
  const macroDescr = getValueByLang(newValue.description, descLang, !onInit);

  macroValues.value = {
    icon: newValue.icon || getDefaultEntityIcon(newValue),
    name: {
      value: macroName.value,
      langCode: macroName.lang
    },
    description: {
      value: macroDescr.value,
      langCode: macroDescr.lang
    }
  };

  selectedEntities.value = newValue.options?.included_entities ?? [];

  if (macroIncludedList.value) {
    macroIncludedList.value.updateSelected(selectedEntities.value);
  }
}

function changeItemIcon(change: ChangeCallbackParams) {
  macroValues.value.icon = change.value as string;

  if (!macro.value || macro.value == null) {
    return;
  }

  const newValues = {
    ...macro.value,
    icon: macroValues.value.icon,
  } as Macro;

  submitChange(newValues);
}

function changeItemName(message: any) {
  if (!macro.value || macro.value == null) {
    return;
  }

  const name = standardizeLangTexts({
    ...(macro.value.name || {}),
    [message.langCode]: message.value,
  }, message.langCode);

  const newValues = {
    ...macro.value,
    name: name,
  } as Macro;

  submitChange(newValues);
}

function changeItemDescription(message: any) {
  if (!macro.value || macro.value == null) {
    return;
  }

  const description = standardizeLangTexts({
    ...(macro.value.description || {}),
    [message.langCode]: message.value,
  }, message.langCode);

  const newValues = {
    ...macro.value,
    description: description,
  } as Macro;

  submitChange(newValues);
}

async function submitChange(message: Macro) {
  if (!macro.value) {
    return;
  }

  try {
    macro.value = await storage.update(
      macro.value.entity_id,
      message
    ) as Macro;
  } catch (e: any) {
    addErrorBottom(e, 'macro.settings.update');
    loadPageData();
  }
}

function changeItemNameLang(lang: string) {
  macroValues.value.name.langCode = lang;

  if (macro.value) {
    macroValues.value.name.value = getValueByLang(macro.value.name, lang, true).value;
  }
}

function changeItemDescriptionLang(lang: string) {
  macroValues.value.description.langCode = lang;
  
  if (macro.value) {
    macroValues.value.description.value = getValueByLang(macro.value.description, lang, true).value;
  }
}

async function setDefaults() {
  fetchFilteredEntities(true);

  try {
    allEntities.value = await integrationsStorage.getConfiguredEntities(null, true);
    instances.value = await integrationsStorage.getInstances();
  } catch (e: any) {
    addErrorBottom(e);
  }
}

async function entityListChanged(newList: IncludedEntity[]) {
  if (!macro.value) {
    return;
  }

  const changedListLength = selectedEntities.value.length != newList.length;
  selectedEntities.value = newList;
  const newValues = {
    options: {
      entity_ids: selectedEntities.value.map((entity) => {
        return entity.entity_id;
      }),
    },
  };

  try {
    macro.value = await storage.update(
      macro.value.entity_id,
      newValues
    ) as Macro;

    if (changedListLength) {
      await fetchFilteredEntities(true);
    }
  } catch (e: any) {
    addErrorBottom(e, 'macro.settings.update');
    loadPageData();
  }
}

function addEntitiesToGroup(entities: ConfiguredEntity[]) {
  const newList = JSON.parse(JSON.stringify(selectedEntities.value)).concat(entities);
  entityListChanged(newList);
}

function reloadEntities() {
  setDefaults();
}

function changeFilter(data: EntityFilterData) {
  entityListFilter.value = data;
  fetchFilteredEntities(true);
}

async function fetchFilteredEntities(userFetchFirstPage: boolean = false) {
  if (userFetchFirstPage === true) {
    pagination.value.page = 1;
  }

  let searchText = entityListFilter.value.searchText;

  try {
    const entList = await integrationsStorage.getConfiguredEntitiesByPageByLimit(entityListFilter.value.instances || '', false, pagination.value.page, pagination.value.limit, searchText, entityListFilter.value.entityTypes, props.macroId);
    filteredEntities.value = entList.data.configuredEntities as ConfiguredEntity[];

    const listHeaders = entList.headers as Headers;
    if (listHeaders) {
      const headerLimit = Number(listHeaders['pagination-limit']);
      const newLimit = headerLimit <= Number(listHeaders['pagination-count']) ? pagination.value.limit : headerLimit;
      pagination.value = {
        count: Number(listHeaders['pagination-count']) || 0,
        limit: newLimit || 0,
        page: Number(listHeaders['pagination-page']) || 0,
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function changePage(value: number) {
  pagination.value.page = value;
  fetchFilteredEntities();
}

function changePerPage(value: number) {
  pagination.value.page = 1;
  pagination.value.limit = value;
  fetchFilteredEntities();
}

async function loadPageData() {
  loading.value = true;
  try {
    const newValue = await storage.getMacro(props.macroId);
    await setDefaults();
    setMacro(newValue, true);
  } catch (e: any) {
    addErrorBottom(e);
  }
  loading.value = false;
}

onMounted(async () => {
  await loadPageData();
})

</script>
<template>
  <div class="ep-settings">
    <div class="ep-settings__form panel-col panel-col--40" v-overflow-indicator>
      <div class="ep-settings__form__wrapper">
        <div class="ep-settings__form__header">
          <IconSelect
            :key="macroValues && macroValues.icon ? macroValues.icon : 'fa-light fa-list-alt'"
            :value="macroValues && macroValues.icon ? macroValues.icon : ''"
            :fallback="'fa-light fa-list-alt'"
            :change-callback="changeItemIcon"
            :has-tv-channel="true"
          />
        </div>
        <UCInput
          v-if="macroValues.name"
          v-model="macroValues.name"
          @submit="changeItemName"
          @changeLang="changeItemNameLang"
          :translations="macro?.name"
          :has-lang="true"
          :full-w="true"
          :label="$t('form.name')"
        />
        <UCInput
          v-if="macroValues.description"
          v-model="macroValues.description"
          @submit="changeItemDescription"
          @changeLang="changeItemDescriptionLang"
          :translations="macro?.description"
          :type="'textarea'"
          :has-lang="true"
          :full-w="true"
          :label="$t('form.description')"
        />
      </div>
    </div>
    <div class="ep-settings__included-entities panel-col panel-col--60">
      <div class="ep-settings__included-entities__wrapper">
        <IncludedEntities
          ref="macroIncludedList"
          @entity-list-changed="entityListChanged"
          @reload-entities="reloadEntities"
          @click-add="showAvailableEntities = true"
          :entities="selectedEntities"
          :instances="instances"
          :show-button-add="showAvailableEntities == false"
          :drag-group="'macro-entities'"
          :loading="loading"
          :text-add-first-descr="$t('macro.edit.empty') + ' ' + $t('entity.add_first_btn_below')"
        />
      </div>
    </div>
    <Transition :name="showAvailableEntities == true ? 'slide-tab-right' : 'slide-tab-left'">
      <div
        v-show="showAvailableEntities == true"
        ref="macroAvailableEntities"
        class="ep-settings__available-entities panel-col panel-col--40"
      >
        <div class="ep-settings__available-entities__wrapper">
          <div class="ep-settings__available-entities__header">
            <button  @click="showAvailableEntities = false" class="button button--blank button--icon">
              <i class="fa-light fa-arrow-left"></i>
            </button>
            <span>{{ $t("activity.edit.add_entities") }}</span>
          </div>
          <EntityListFiltered
            @add-entities="addEntitiesToGroup"
            @change-filter="changeFilter"
            @reload-entities="reloadEntities"
            @change-page="changePage"
            @change-per-page="changePerPage"
            :all-entities="filteredEntities"
            :instances="instances"
            :pagination="pagination"
            :drag-group="'macro-entities'"
            :has-quick-options="true"
            :parent="'edit-macro'"
            :exclude-entity-types="[EntityType.sensor]"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>
